// Import necessary modules from Firebase

import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { NotificationService } from "./Services/Notification";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// ─── FCM Messaging ────────────────────────────────────────

let messaging = null;
try {
  messaging = getMessaging(app);
} catch (err) {
  console.warn("Firebase Messaging not supported in this browser:", err);
}

const getDeviceMetadata = async () => {
  const ua = navigator.userAgent;
  let browser = "unknown";
  let browserVersion = "";

  if (ua.includes("Firefox/")) {
    browser = "Firefox";
    browserVersion = ua.split("Firefox/")[1]?.split(" ")[0] || "";
  } else if (ua.includes("Edg/")) {
    browser = "Edge";
    browserVersion = ua.split("Edg/")[1]?.split(" ")[0] || "";
  } else if (ua.includes("Chrome/")) {
    browser = "Chrome";
    browserVersion = ua.split("Chrome/")[1]?.split(" ")[0] || "";
  } else if (ua.includes("Safari/")) {
    browser = "Safari";
    browserVersion = ua.split("Version/")[1]?.split(" ")[0] || "";
  }

  let os = "unknown";
  let osVersion = "";
  if (ua.includes("Windows")) {
    os = "Windows";
    osVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1] || "";
  } else if (ua.includes("Mac OS X")) {
    os = "macOS";
    osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
  } else if (ua.includes("Android")) {
    os = "Android";
    osVersion = ua.match(/Android ([\d.]+)/)?.[1] || "";
  } else if (ua.includes("iPhone") || ua.includes("iPad")) {
    os = "iOS";
    osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
  } else if (ua.includes("Linux")) {
    os = "Linux";
  }

  let deviceType = "desktop";
  if (/Mobi|Android/i.test(ua)) deviceType = "mobile";
  else if (/Tablet|iPad/i.test(ua)) deviceType = "tablet";

  // Get geolocation from IP
  let city = null;
  let country = null;
  try {
    const geoResponse = await fetch("https://ipapi.co/json/");
    if (geoResponse.ok) {
      const geoData = await geoResponse.json();
      city = geoData.city || null;
      country = geoData.country_name || null;
    }
  } catch (geoErr) {
    console.warn("⚠️ Could not fetch geolocation:", geoErr.message);
  }

  return {
    browser,
    browserVersion,
    os,
    osVersion,
    deviceType,
    platform: "web",
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language || navigator.userLanguage || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    city,
    country,
    deviceId:
      localStorage.getItem("pb_device_id") || generateAndStoreDeviceId(),
  };
};

const generateAndStoreDeviceId = () => {
  const id =
    "pb_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).substring(2, 10);
  localStorage.setItem("pb_device_id", id);
  return id;
};

// ─── Notification Prompt Interval Management ─────────────────────────

const NOTIFICATION_PROMPT_KEY = "pb_last_notification_prompt";
const NOTIFICATION_PROMPT_INTERVAL = 20 * 24 * 60 * 60 * 1000; // 15 days in milliseconds

export const shouldShowNotificationPrompt = () => {
  if ("Notification" in window && Notification.permission !== "default") {
    return false; // Don't show if already granted or denied
  }

  const lastPrompt = localStorage.getItem(NOTIFICATION_PROMPT_KEY);
  if (!lastPrompt) {
    return true; // First time, show the prompt
  }

  const lastPromptTime = parseInt(lastPrompt, 10);
  const timeSinceLastPrompt = Date.now() - lastPromptTime;

  return timeSinceLastPrompt >= NOTIFICATION_PROMPT_INTERVAL;
};

export const recordNotificationPromptShown = () => {
  localStorage.setItem(NOTIFICATION_PROMPT_KEY, Date.now().toString());
};

// ─── FCM Token Refresh Management ────────────────────────────────────

const FCM_TOKEN_KEY = "pb_fcm_token";

const getStoredFCMToken = () => {
  return localStorage.getItem(FCM_TOKEN_KEY);
};

const storeFCMToken = (token) => {
  localStorage.setItem(FCM_TOKEN_KEY, token);
};

const deleteDbAsync = (dbName) => {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(false);
    try {
      const req = indexedDB.deleteDatabase(dbName);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => {
        console.warn(`⚠️ Deletion of IndexedDB '${dbName}' blocked by active connection`);
        resolve(false);
      };
      setTimeout(() => resolve(false), 1000);
    } catch {
      resolve(false);
    }
  });
};

const clearFCMIndexedDB = async () => {
  const dbsToDelete = [
    "firebase-messaging-database",
    "firebase-installations-database",
    "fcm_token_details_db",
  ];
  if (typeof indexedDB !== "undefined" && indexedDB.databases) {
    try {
      const existingDbs = await indexedDB.databases();
      for (const db of existingDbs) {
        if (
          db.name &&
          (db.name.includes("firebase") || db.name.includes("fcm"))
        ) {
          if (!dbsToDelete.includes(db.name)) {
            dbsToDelete.push(db.name);
          }
        }
      }
    } catch {
      // ignore
    }
  }
  await Promise.allSettled(dbsToDelete.map((name) => deleteDbAsync(name)));
};

const getSafelyToken = async (messaging, options) => {
  try {
    return await getToken(messaging, options);
  } catch (error) {
    const isVersionError =
      error?.name === "VersionError" ||
      (typeof error?.message === "string" &&
        (error.message.includes("VersionError") ||
          error.message.includes("less than the existing version")));

    if (isVersionError) {
      console.warn(
        "⚠️ FCM IndexedDB VersionError detected, clearing stale databases...",
      );
      try {
        await clearFCMIndexedDB();
        await new Promise((resolve) => setTimeout(resolve, 500));
        return await getToken(messaging, options);
      } catch (retryErr) {
        console.warn("⚠️ FCM token retrieval retry failed after clearing IndexedDB:", retryErr);
        return null;
      }
    }
    throw error;
  }
};

export const refreshFCMToken = async (userToken) => {
  try {
    if (!messaging || !userToken) {
      console.warn(
        "❌ Cannot refresh token: messaging or user token unavailable",
      );
      return null;
    }

    if ("Notification" in window && Notification.permission !== "granted") {
      return null;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn("VAPID key not configured");
      return null;
    }

    // Wait for Service Worker to be ready before attempting subscription
    let swRegistration = null;
    if ("serviceWorker" in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.warn("⚠️ Service Worker not ready, but continuing:", swError);
      }
    }

    // Get the new token
    const newToken = await getSafelyToken(messaging, {
      vapidKey,
      ...(swRegistration && { serviceWorkerRegistration: swRegistration }),
    });
    const oldToken = getStoredFCMToken();
    const oldUserToken = localStorage.getItem("pb_fcm_user_token");

    // If token is the same AND user is the same, no need to update
    if (oldToken === newToken && oldUserToken === userToken) {
      return newToken;
    }

    // Register new token (backend will update old one if exists, or create new)
    const metadata = await getDeviceMetadata();
    await NotificationService.registerToken(
      { fcmToken: newToken, ...metadata },
      userToken,
    );

    storeFCMToken(newToken);
    localStorage.setItem("pb_fcm_user_token", userToken);
    return newToken;
  } catch (error) {
    console.error("❌ Error refreshing FCM token:", error);
    return null;
  }
};

export const requestNotificationPermission = async (token) => {
  try {
    if (!messaging) {
      console.warn("Messaging not available");
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      recordNotificationPromptShown(); // Record even if denied
      return null;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn("VAPID key not configured");
      return null;
    }

    // Wait for Service Worker to be ready before attempting subscription
    let swRegistration = null;
    if ("serviceWorker" in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.warn("⚠️ Service Worker not ready, but continuing:", swError);
      }
    }

    const fcmToken = await getSafelyToken(messaging, {
      vapidKey,
      ...(swRegistration && { serviceWorkerRegistration: swRegistration }),
    });

    if (fcmToken && token) {
      const metadata = await getDeviceMetadata();
      await NotificationService.registerToken(
        { fcmToken, ...metadata },
        token,
      );

      // Store the token in localStorage
      storeFCMToken(fcmToken);
      localStorage.setItem("pb_fcm_user_token", token);

      // Record that we showed the prompt
      recordNotificationPromptShown();
    }

    return fcmToken;
  } catch (error) {
    console.error("❌ Error requesting notification permission:", error);
    // Provide helpful debugging info
    if (error.name === "AbortError") {
      console.error(
        "💡 Hint: Service Worker issue. Try refreshing the page or checking browser DevTools > Application > Service Workers.",
      );
    }
    return null;
  }
};

export const onMessageListener = (callback) => {
  if (!messaging) {
    console.warn("Messaging not available");
    return null;
  }
  return onMessage(messaging, (payload) => {
    if (callback) callback(payload);
  });
};

export { auth, RecaptchaVerifier, signInWithPhoneNumber, messaging };
